import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useMemo, useState, type ReactNode } from "react";
import { Navigate, useNavigate } from "react-router-dom";

import { useAuth } from "@bw/shared/hooks";
import { AccountNameEditor } from "../AccountNameEditor";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useMyOrg, useOnboardingState } from "@/hooks/use-my-org";
import {
  useOrgFacilitiesQuery,
  useSelectedFacility,
} from "@/app/FacilityContext";
import { getFacility, type FacilityDetail } from "@/libs/facilityApi";
import {
  goLive,
  orgId as readOrgId,
  registerOrg,
  submitOrgForReview,
  type ContractTerms,
  type OnboardingState,
  type OnboardingStep,
  type Org,
  type OrgRegistrationInput,
  type OrgStatus,
} from "@/libs/onboardingApi";
import {
  CreateBusinessForm,
  type CreateBusinessFormValues,
} from "./create-business-form";
import { BusinessProfilePanel } from "./panels/BusinessProfilePanel";
import { BusinessAddressPanel } from "./panels/BusinessAddressPanel";
import {
  FacilityIdentityEditor,
  type FacilityIdentitySaveResult,
} from "@/components/address/FacilityIdentityEditor";
import { WaiverPanel } from "./panels/WaiverPanel";
import { StripeConnectPanel } from "./panels/StripeConnectPanel";
import { ContractTermsPanel } from "./panels/ContractTermsPanel";
import { PlatformReviewPanel } from "./panels/PlatformReviewPanel";
import { FormMessage } from "./panels/shared";
import { PrivateCourtSetupCard } from "./private-court-setup-card";

// ---------------------------------------------------------------------------
// Label maps — the backend emits its own section + service vocabulary; the FE
// re-labels them for the operator-facing workspace. Unknown keys fall through to
// a humanized version of the raw key (never the raw snake_case).
// ---------------------------------------------------------------------------

const SECTION_LABELS: Record<string, string> = {
  business_setup: "Business account",
  compliance: "Player requirements",
  facility_footprint: "Facility",
};

const SERVICE_LABELS: Record<string, string> = {
  court_reservation: "Reservations",
  open_play: "Drop-in",
  memberships: "Memberships",
  classes: "Classes",
  coaching: "Coaching",
};

// Steps emitted for platform tracking that do not need an inline editor here.
const NON_REVIEW_STEP_KEYS = new Set<string>([
  "confirm_staff_check_in_support",
]);

function humanize(key: string): string {
  return key
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function sectionLabel(key: string): string {
  return SECTION_LABELS[key] ?? humanize(key);
}

function serviceLabel(service: string): string {
  return SERVICE_LABELS[service] ?? humanize(service);
}

// A step that is required for approval and is an actionable item (NOT the
// platform-approval gate itself) — these form the progress denominator.
function isApprovalRequiredStep(step: OnboardingStep): boolean {
  return (
    Boolean(step.required_for_approval) && step.key !== "platform_approval"
  );
}

// Steps surfaced as review items on the page. platform_approval IS shown
// (it carries the "Waiting" pill); the staff-check-in confirmation is not.
function isReviewVisibleStep(step: OnboardingStep): boolean {
  if (NON_REVIEW_STEP_KEYS.has(step.key)) return false;
  return Boolean(step.required_for_approval);
}

function stepStatus(
  step: OnboardingStep,
): "Complete" | "Waiting" | "Needs action" {
  if (step.complete) return "Complete";
  if (step.key === "platform_approval") return "Waiting";
  return "Needs action";
}

// The inline editor for a step. F3c-c1 owns the business profile, HQ address,
// facility identity, and waiver editors; F3c-c2 adds the Stripe Connect,
// contract-terms, and platform-review panels. The remaining tracked items render
// a neutral note.
function renderPanel(
  step: OnboardingStep,
  org: Org,
  orgId: string,
  facility: FacilityDetail | null,
  ctx: {
    terms: ContractTerms | null;
    ownerName: string;
    allSetupComplete: boolean;
    platformApproved: boolean;
    status: OrgStatus;
    onFacilitySaved: (result: FacilityIdentitySaveResult) => void;
  },
): ReactNode {
  switch (step.key) {
    case "business_legal_profile":
      return <BusinessProfilePanel org={org} orgId={orgId} />;
    case "headquarters_address":
      return <BusinessAddressPanel org={org} orgId={orgId} />;
    case "connect_stripe":
      return <StripeConnectPanel org={org} orgId={orgId} />;
    case "review_contract_terms":
      return (
        <ContractTermsPanel
          org={org}
          orgId={orgId}
          terms={ctx.terms}
          ownerName={ctx.ownerName}
        />
      );
    case "create_facility":
      return (
        <Stack spacing={1.5}>
          <FacilityIdentityEditor
            organizationId={orgId}
            businessAddress={org.address}
            facility={facility}
            onSaved={ctx.onFacilitySaved}
          />
          <Typography color="text.secondary" variant="body2">
            You can add more facilities after your organization is approved.
          </Typography>
        </Stack>
      );
    case "publish_waiver":
      return <WaiverPanel orgId={orgId} facility={facility} />;
    case "platform_approval":
      return (
        <PlatformReviewPanel
          status={ctx.status}
          allSetupComplete={ctx.allSetupComplete}
          platformApproved={ctx.platformApproved}
        />
      );
    default:
      return (
        <Typography color="text.secondary" variant="body2">
          This item is tracked by platform review and is set up elsewhere in
          your workspace.
        </Typography>
      );
  }
}

// Status is conveyed by MUI color + label text, not colour alone.
function StatusPill({ step }: { step: OnboardingStep }) {
  const status = stepStatus(step);
  const color =
    status === "Complete"
      ? "success"
      : status === "Waiting"
        ? "info"
        : "warning";
  return <Chip color={color} label={status} size="small" variant="outlined" />;
}

// The inline block for a single review step: the step label + status pill +
// optional service pills + description as a header, with the step's real editor
// (renderPanel) rendered directly beneath it — always visible. Getting Started is
// a single top-to-bottom form the operator fills in directly, not a set of
// collapsible accordions, so there is no expand/collapse affordance here. Styled
// to match the Card visual language (rounded-xl border bg-card shadow-sm).
function ReviewStep({
  step,
  children,
}: {
  step: OnboardingStep;
  children: ReactNode;
}) {
  return (
    <Paper component="section" variant="outlined" sx={{ overflow: "hidden" }}>
      <Box sx={{ px: 2, py: 2 }}>
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: "center", flexWrap: "wrap" }}
        >
          <Typography
            component="h3"
            variant="subtitle1"
            sx={{ fontWeight: 700 }}
          >
            {step.label}
          </Typography>
          <StatusPill step={step} />
        </Stack>
        {step.applies_to_services?.length ? (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
            {step.applies_to_services.map((service) => (
              <Chip
                color="primary"
                key={service}
                label={serviceLabel(service)}
                size="small"
                variant="outlined"
              />
            ))}
          </Box>
        ) : null}
        {!step.complete && step.description ? (
          <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>
            {step.description}
          </Typography>
        ) : null}
      </Box>
      <Box sx={{ borderTop: 1, borderColor: "divider", px: 2, py: 2.5 }}>
        {children}
      </Box>
    </Paper>
  );
}

export function GettingStarted() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { setFacility: setSelectedFacility } = useSelectedFacility();
  const navigate = useNavigate();
  const orgView = useMyOrg();
  const org = orgView.org ?? null;
  const orgId = org ? readOrgId(org) : null;

  const stateQuery = useOnboardingState(orgId);
  const state: OnboardingState | null = stateQuery.data ?? null;

  // The org's facilities; the first is the one the inline facility/waiver panels
  // operate on (F3c is single-facility onboarding).
  const { data: facilities } = useOrgFacilitiesQuery(orgId);
  const reviewFacilityId = facilities?.[0]?.facility_id ?? null;
  const { data: reviewFacilityDetail } = useQuery({
    queryKey: ["facility", reviewFacilityId],
    queryFn: () => getFacility(reviewFacilityId as string),
    enabled: Boolean(reviewFacilityId),
  });
  const reviewFacility: FacilityDetail | null = reviewFacilityDetail ?? null;
  const onFacilitySaved = (result: FacilityIdentitySaveResult) => {
    const saved = result.facility;
    setSelectedFacility(
      saved.facility_id,
      saved.name,
      saved.facility_type ?? null,
    );
    void queryClient.invalidateQueries({
      queryKey: ["org-facilities", orgId],
    });
    void queryClient.invalidateQueries({
      queryKey: ["org", orgId, "onboarding-state"],
    });
    if (result.kind === "updated") {
      queryClient.setQueryData(
        ["facility", result.facility.facility_id],
        result.facility,
      );
    }
  };

  const [createError, setCreateError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  // Group the visible review steps by section, dropping empty sections.
  const sections = useMemo(() => {
    if (!state) return [];
    return state.sections
      .map((section) => ({
        key: section.key,
        steps: state.steps.filter(
          (step) =>
            step.section_key === section.key && isReviewVisibleStep(step),
        ),
      }))
      .filter((section) => section.steps.length > 0);
  }, [state]);

  const allSteps = useMemo(
    () => sections.flatMap((section) => section.steps),
    [sections],
  );
  const approvalSteps = allSteps.filter(isApprovalRequiredStep);
  const completedCount = approvalSteps.filter((step) => step.complete).length;
  const totalCount = approvalSteps.length;
  const progress =
    totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  // The platform-approval gate itself (separate from the approval-REQUIRED items
  // that form the progress denominator). `platformApproved` reflects whether the
  // platform has already signed off; `allSetupComplete` is every required item
  // done; `contractAccepted` is the commercial-terms signature.
  const platformApproved =
    state?.steps.find((step) => step.key === "platform_approval")?.complete ??
    false;
  const allSetupComplete =
    approvalSteps.length > 0 && approvalSteps.every((step) => step.complete);
  const contractTerms: ContractTerms | null = state?.contract_terms ?? null;
  const contractAccepted = Boolean(contractTerms?.accepted_at);
  const ownerName =
    user?.displayName ?? org?.owner_full_name ?? "Business Owner";

  // The submit-for-review gate: an EXPLICIT button (never auto-submit) shown only
  // when the org is still pending, every required item is done, AND the
  // commercial terms are accepted.
  const submitReviewMutation = useMutation({
    mutationFn: (id: string) => submitOrgForReview(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["org", "me"] });
      if (orgId) {
        void queryClient.invalidateQueries({
          queryKey: ["org", orgId, "onboarding-state"],
        });
      }
    },
  });
  const readyToSubmitReview =
    org?.status === "pending" && allSetupComplete && contractAccepted;

  // The go-live gate: an APPROVED org can go live anytime — the backend has no
  // remaining operational prerequisite once the org is approved (see
  // OrganizationService.go_live). This stays a card ON the workflow (no
  // separate page) — the confirm dialog below is the only extra step.
  const [goLiveConfirmOpen, setGoLiveConfirmOpen] = useState(false);

  const goLiveMutation = useMutation({
    mutationFn: () => goLive(orgId as string),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["org", "me"] });
      if (orgId) {
        void queryClient.invalidateQueries({
          queryKey: ["org", orgId, "onboarding-state"],
        });
      }
      navigate("/dashboard");
    },
  });

  // Build the registration payload from the wizard fields + accepted legal
  // versions + the owner's contact identity. Verified HQ coordinates flow through
  // verbatim so the platform records the matched legal location.
  const onCreateBusiness = async (values: CreateBusinessFormValues) => {
    setCreating(true);
    setCreateError(null);
    try {
      const payload: OrgRegistrationInput = {
        business_name: values.business_name.trim(),
        legal_name: values.legal_name.trim(),
        country: values.country.trim().toUpperCase(),
        address_line1: values.address_line1.trim(),
        address_line2: values.address_line2?.trim() || null,
        city: values.city.trim(),
        state: values.state.trim(),
        postal_code: values.postal_code.trim(),
        latitude: values.latitude ?? null,
        longitude: values.longitude ?? null,
        ...(values.tax_id?.trim() ? { tax_id: values.tax_id.trim() } : {}),
        business_categories: values.business_categories ?? [],
        owner_full_name:
          values.owner_full_name?.trim() || user?.displayName?.trim() || "",
        owner_email: values.owner_email?.trim() || user?.email?.trim() || "",
        owner_phone: values.owner_phone?.trim() || null,
      };
      await registerOrg(payload);
      // Org now exists — invalidate the org-status seam so it re-resolves and the
      // page drops into the inline checklist in-place (no full reload).
      await queryClient.invalidateQueries({ queryKey: ["org"] });
    } catch (err) {
      setCreateError(
        err instanceof Error ? err.message : "Could not create your business",
      );
    } finally {
      setCreating(false);
    }
  };

  // Do not substitute a registration/legal name for the provider's self-chosen
  // account label. This personal-only editor is reachable even when terms fail.
  if (user?.email && !user.displayName?.trim()) {
    return (
      <Box sx={{ maxWidth: 640, mx: "auto", px: 2, py: 3 }}>
        <Typography component="h1" variant="h5" sx={{ mb: 2 }}>
          Complete your account name
        </Typography>
        <AccountNameEditor />
      </Box>
    );
  }

  // Org status still resolving → spinner (don't decide a redirect or wizard yet).
  if (orgView.kind === "loading") {
    return (
      <Box sx={{ display: "grid", minHeight: "50vh", placeItems: "center" }}>
        <CircularProgress aria-label="Loading organization" />
      </Box>
    );
  }

  // An approved org stays on this workflow and goes live inline; only an
  // active org redirects to the dashboard.
  if (org?.status === "active") {
    return <Navigate to="/dashboard" replace />;
  }

  // State still in flight for a known (locked) org → spinner.
  if (orgId && stateQuery.isLoading) {
    return (
      <Box sx={{ display: "grid", minHeight: "50vh", placeItems: "center" }}>
        <CircularProgress aria-label="Loading setup checklist" />
      </Box>
    );
  }

  // No org yet → the registration wizard is the entry point.
  if (orgView.kind === "no-org") {
    return (
      <CreateBusinessForm
        onSubmit={onCreateBusiness}
        error={createError}
        submitting={creating}
      />
    );
  }

  // Org-status read failed hard (non-404) → honest error.
  if (orgView.kind === "error") {
    return (
      <Box role="alert" sx={{ color: "error.main", px: { xs: 2, lg: 3 } }}>
        {orgView.message}
      </Box>
    );
  }

  // Onboarding-state read failed → honest error.
  if (org && stateQuery.error && !state) {
    return (
      <Box role="alert" sx={{ color: "error.main", px: { xs: 2, lg: 3 } }}>
        We couldn&apos;t load your setup checklist. Please try again.
      </Box>
    );
  }

  if (!org || !orgId || !state) {
    return (
      <Box sx={{ color: "text.secondary", px: { xs: 2, lg: 3 } }}>
        We couldn&apos;t load your setup checklist. Please try again.
      </Box>
    );
  }

  return (
    <Stack spacing={3} sx={{ px: { xs: 2, lg: 3 }, pb: 5 }}>
      {org.status === "approved" ? (
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Box sx={{ maxWidth: 672 }}>
                <Typography
                  component="h1"
                  variant="h4"
                  sx={{ fontWeight: 700 }}
                >
                  Business review approved
                </Typography>
                <Typography
                  color="text.secondary"
                  variant="body2"
                  sx={{ mt: 1 }}
                >
                  Activate your business workspace when you&apos;re ready.
                  Player discovery and bookings depend on facility approval and
                  service readiness. Online payments still require completed
                  payment setup.
                </Typography>
              </Box>
              <Box>
                <Button
                  disabled={goLiveMutation.isPending}
                  onClick={() => setGoLiveConfirmOpen(true)}
                  size="small"
                  startIcon={<RocketLaunchRoundedIcon />}
                  variant="contained"
                >
                  Activate workspace
                </Button>
              </Box>
              {goLiveMutation.isError ? (
                <FormMessage tone="error">
                  Could not activate your workspace. Please try again or contact
                  support.
                </FormMessage>
              ) : null}
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent>
            <Stack spacing={2.5}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={3}
                sx={{
                  alignItems: { md: "flex-end" },
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ maxWidth: 672 }}>
                  <Typography
                    component="h1"
                    variant="h4"
                    sx={{ fontWeight: 700 }}
                  >
                    Finish business review setup
                  </Typography>
                  <Typography
                    color="text.secondary"
                    variant="body2"
                    sx={{ mt: 1 }}
                  >
                    {state.display.subtitle ??
                      "Complete the required items before submitting for platform review."}
                  </Typography>
                </Box>
                <Box sx={{ minWidth: 220 }}>
                  <Typography
                    variant="h3"
                    sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}
                  >
                    {completedCount}/{totalCount}
                  </Typography>
                  <Typography
                    color="text.secondary"
                    variant="caption"
                    sx={{ display: "block", mt: 0.5 }}
                  >
                    review items
                  </Typography>
                  <LinearProgress
                    aria-label="Business review setup progress"
                    value={progress}
                    variant="determinate"
                    sx={{ height: 10, mt: 1.5, borderRadius: 999 }}
                  />
                </Box>
              </Stack>
              {readyToSubmitReview ? (
                <Box>
                  <Button
                    disabled={submitReviewMutation.isPending}
                    onClick={() => submitReviewMutation.mutate(orgId)}
                    size="small"
                    variant="contained"
                  >
                    {submitReviewMutation.isPending
                      ? "Submitting..."
                      : "Submit for Review"}
                  </Button>
                </Box>
              ) : null}
              {submitReviewMutation.isError ? (
                <FormMessage tone="error">
                  Could not submit for review. Check the required items and try
                  again.
                </FormMessage>
              ) : null}
            </Stack>
          </CardContent>
        </Card>
      )}

      {reviewFacility &&
      (org.status === "pending" || org.status === "in_review") ? (
        <PrivateCourtSetupCard facility={reviewFacility} />
      ) : null}

      <Stack spacing={3}>
        {sections.map((section) => (
          <Card key={section.key}>
            <CardHeader
              title={
                <Typography
                  component="h2"
                  variant="h6"
                  sx={{ fontWeight: 700 }}
                >
                  {sectionLabel(section.key)}
                </Typography>
              }
              action={
                <Chip
                  color="primary"
                  label={
                    <>
                      {section.steps.length}{" "}
                      {section.steps.length === 1 ? "item" : "items"}
                    </>
                  }
                  size="small"
                  variant="outlined"
                />
              }
              sx={{ borderBottom: 1, borderColor: "divider" }}
            />
            <CardContent>
              <Stack spacing={1.5}>
                {section.steps.map((step) => (
                  <ReviewStep key={step.key} step={step}>
                    {renderPanel(step, org, orgId, reviewFacility, {
                      terms: contractTerms,
                      ownerName,
                      allSetupComplete,
                      platformApproved,
                      status: org.status,
                      onFacilitySaved,
                    })}
                  </ReviewStep>
                ))}
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>

      {sections.length === 0 ? (
        <FormMessage tone="success">
          Your setup is complete. We&apos;ll notify you about platform review.
        </FormMessage>
      ) : null}

      <Dialog
        open={goLiveConfirmOpen}
        onClose={() => setGoLiveConfirmOpen(false)}
      >
        <DialogTitle>Activate workspace?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Start using your business workspace. Player discovery and bookings
            depend on facility approval and service readiness. Online payments
            still require completed payment setup.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={goLiveMutation.isPending}
            onClick={() => setGoLiveConfirmOpen(false)}
            size="small"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            disabled={goLiveMutation.isPending}
            onClick={() => goLiveMutation.mutate()}
            size="small"
            variant="contained"
          >
            {goLiveMutation.isPending ? "Activating…" : "Activate workspace"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
